// import localforage from 'localforage';
import { openDB } from 'idb';
import localForage from 'localforage';
import { storageKeys } from '@/constants';

/**
 * Storage helper functions for managing gift flow data
 */

const GIFT_FLOW_KEY =
  /* process.env.REACT_APP_GIFT_FLOW_KEY ?? */ 'giftFlowData';

  const BLOB_STORE_VERSION = import.meta.env.VITE_BLOB_STORE_VERSION ?? 1;

/**
 * Save gift flow data to sessionStorage
 * @param {Object} data - The gift flow data to save
 */
export const saveGiftFlowData = (data: object) => {
  try {
    const existingData = getGiftFlowData();
    const updatedData = { ...existingData, ...data };
    sessionStorage.setItem(GIFT_FLOW_KEY, JSON.stringify(updatedData));
  } catch (error) {
    console.error('Error saving gift flow data:', error);
  }
};

/**
 * Get gift flow data from sessionStorage
 * @returns {Object} The gift flow data or empty object
 */
export const getGiftFlowData = () => {
  try {
    const data = sessionStorage.getItem(GIFT_FLOW_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error getting gift flow data:', error);
    return {};
  }
};

/**
 * Clear gift flow data from sessionStorage
 */
export const clearGiftFlowData = () => {
  try {
    sessionStorage.removeItem(GIFT_FLOW_KEY);
  } catch (error) {
    console.error('Error clearing gift flow data:', error);
  }
};

/**
 * Update a specific field in gift flow data
 * @param {string} key - The field key to update
 * @param {any} value - The value to set
 */
export const updateGiftFlowField = (key: string, value: unknown) => {
  try {
    const data = getGiftFlowData();
    data[key] = value;
    sessionStorage.setItem(GIFT_FLOW_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error updating gift flow field:', error);
  }
};

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const base64ToBlob = (base64: string): Blob => {
  const [header, data] = base64.split(',');
  const mimeType = header.match(/:(.*?);/)?.[1] ?? '';
  const byteCharacters = atob(data);
  const byteArray = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteArray[i] = byteCharacters.charCodeAt(i);
  }

  return new Blob([byteArray], { type: mimeType });
};

export const blobToFile = (blob: Blob, fileName: string): File =>
  new File([blob], fileName, { type: blob.type });

export const fileToBlob = (file: File): Blob =>
  new Blob([file], { type: file.type });

export const mimeToExt: Record<string, string> = Object.freeze({
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/svg+xml': 'svg',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  // AAC in an MP4 container — the iOS-playable target for voice gifts.
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  // Kept for any non-voice/legacy use; voice uploads must not be WebM (iOS
  // cannot decode it for Memory Vault playback).
  'audio/webm': 'webm',
  'application/pdf': 'pdf',
});

export const getExtensionFromBlob = (blob: Blob): string => {
  return mimeToExt[blob.type] ?? blob.type.split('/')[1] ?? 'bin';
};

/**
 * Content types a voice upload is allowed to declare. Each is a format that
 * iOS/Flutter can decode and that the API maps to a real extension
 * (`audio/mp4` → `.m4a`, `audio/mpeg` → `.mp3`, `audio/wav` → `.wav`).
 * WebM is deliberately excluded — see the -11800 playback issue.
 */
export const ALLOWED_AUDIO_UPLOAD_TYPES = Object.freeze([
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
] as const);

/**
 * Resolves the content type for a voice upload, requiring the recorder to have
 * produced an explicitly allowlisted type. There is no silent `audio/wav`
 * fallback: a blob with an empty or unsupported type would have the upload
 * mislabel its bytes (the exact bug that broke iPhone playback), so it fails
 * loudly instead.
 */
export const resolveAudioUploadContentType = (blob: Blob): string => {
  const type = blob.type;
  if (
    !type ||
    !ALLOWED_AUDIO_UPLOAD_TYPES.includes(
      type as (typeof ALLOWED_AUDIO_UPLOAD_TYPES)[number],
    )
  ) {
    throw new Error(
      `Unsupported audio content type "${type || '(empty)'}". Expected one of: ${ALLOWED_AUDIO_UPLOAD_TYPES.join(', ')}.`,
    );
  }
  return type;
};

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const db = openDB('media', BLOB_STORE_VERSION, {
  upgrade(db, /*oldVersion, newVersion*/) {
    // Create the store if it doesn't exist (handles v1 and any new versions)
    if (!db.objectStoreNames.contains('blobs')) {
      db.createObjectStore('blobs');
    }
  },
  blocked(currentVersion, blockedVersion) {
    console.warn(`IndexedDB blocked: current ${currentVersion}, blocked ${blockedVersion}`)
  },
  blocking() {
    console.warn('IndexedDB blocking another version from opening')
  },
})

type StorageKey = keyof typeof storageKeys;

const handleStorageError = (operation: string, error: unknown): never => {
  const message =
    error instanceof Error ? error.message : 'Unknown storage error';
  console.error(`blobStorage.${operation} failed:`, message);
  throw error;
};

export const blobStorage = {
  set: async (key: StorageKey, blob: Blob): Promise<void> => {
    await (await db)
      .put('blobs', blob, key);
      // .catch((e) => console.log('set', e));
  },

  get: async (key: StorageKey): Promise<Blob | null> =>
    (await db)
      .get('blobs', key)
      .then((v) => v ?? null)
      .catch((e) => handleStorageError('get', e)),

  remove: async (key: StorageKey): Promise<void> =>
    (await db)
      .delete('blobs', key)
      .catch((e) => handleStorageError('remove', e)),

  clear: async (): Promise<void> =>
    (await db).clear('blobs').catch((e) => handleStorageError('clear', e)),
};

export async function checkStorage(): Promise<boolean> {
  try {
    await localForage.setItem('__storage_test__', 'test')
    await localForage.removeItem('__storage_test__')
    return true
  } catch (error) {
    console.error('Storage unavailable:', error)
    return false
  }
}

export async function checkStorageQuota(): Promise<boolean> {
  if (!navigator.storage?.estimate) return true // API not available, assume ok
  
  const { usage = 0, quota = 0 } = await navigator.storage.estimate()
  const percentUsed = (usage / quota) * 100
  
  if (percentUsed > 90) {
    console.warn(`Storage is ${percentUsed.toFixed(1)}% full`)
    return false
  }
  
  return true
}
